import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Lock, Film, Loader2, KeyRound } from "lucide-react";
import CinemaBrand from "@/components/CinemaBrand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PinGate — Sperrt die öffentliche Landing-Page hinter einem numerischen/textuellen PIN.
 * Zeigt eine minimale, cinematische Vollbild-Wall. Bei Erfolg: onSuccess(pin) callback.
 * Der Aufrufer speichert den PIN dauerhaft (localStorage) und lädt neu.
 */
export default function PinGate({ onSuccess }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Fokus auf Eingabefeld für schnelles Tippen
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const val = (pin || "").trim();
    if (!val) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/public/pin-check`, { pin: val });
      if (data?.valid) {
        onSuccess(data.token || val);
      } else {
        throw new Error("invalid");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Falscher Code. Versuch's nochmal.");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0806] text-[#F5F0E6] font-body flex flex-col items-center justify-center px-6 relative overflow-hidden"
      data-testid="landing-pin-gate"
    >
      {/* Cinematischer Hintergrund */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(212,175,55,0.08) 0%, transparent 60%)",
        }}
      />
      {/* Diagonale Filmrolle-Deko */}
      <Film
        size={280}
        strokeWidth={0.4}
        className="absolute -top-16 -right-20 text-[#D4AF37]/10 rotate-12 pointer-events-none"
      />
      <Film
        size={220}
        strokeWidth={0.4}
        className="absolute -bottom-16 -left-16 text-[#D4AF37]/10 -rotate-12 pointer-events-none"
      />

      <div className="relative z-10 w-full max-w-md text-center">
        <div className="mb-10 flex flex-col items-center gap-4">
          <CinemaBrand size="lg" stacked />
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">
            Privater Filmpalast
          </div>
        </div>

        <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
          <Lock size={26} className="text-[#D4AF37]" />
        </div>

        <h1 className="font-heading text-3xl md:text-4xl mb-3">Nur für Freunde des Hauses.</h1>
        <p className="text-sm text-[#A8A398] mb-10 max-w-sm mx-auto leading-relaxed">
          Dies ist ein privates Heimkino. Gib den Zugangs-Code ein, den du von uns bekommen
          hast — der Vorhang öffnet sich für dich.
        </p>

        <form onSubmit={submit} className={shake ? "animate-shake" : ""}>
          <div className="relative mb-4">
            <KeyRound
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#575148]"
            />
            <input
              ref={inputRef}
              type="password"
              inputMode="text"
              autoComplete="off"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                if (error) setError("");
              }}
              placeholder="Zugangs-Code"
              data-testid="landing-pin-gate-input"
              className="w-full bg-[#050505] border border-[#2A2A2A] rounded-full pl-11 pr-4 py-4 text-center text-lg tracking-[0.5em] font-mono outline-none focus:border-[#D4AF37] focus:bg-[#0A0A0A] transition-colors"
              disabled={loading}
            />
          </div>
          {error && (
            <div
              className="text-xs text-[#EE9CA1] uppercase tracking-widest mb-4"
              data-testid="landing-pin-gate-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !pin.trim()}
            data-testid="landing-pin-gate-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D4AF37] text-black rounded-full text-xs uppercase tracking-[0.4em] hover:bg-[#EEDC82] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Prüfe …
              </>
            ) : (
              <>Vorhang öffnen</>
            )}
          </button>
        </form>

        <div className="mt-10 text-[10px] uppercase tracking-[0.4em] text-[#575148]">
          Code vergessen? Frag den Kinobetreiber.
        </div>
      </div>

      {/* Shake-Animation */}
      <style>{`
        @keyframes mh-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake { animation: mh-shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
